// SEQ-180 legibility follow-up (15 September 2026): the parts every phase 1
// chart card is built from.
//
// WHY A SHARED FRAME. The five phase 1 widgets predate the generic widget
// renderer and each grew its own header, its own empty state and its own
// spacing, which is how two of them ended up with no empty state at all and
// how the axis toggle ended up styling itself from a chart colour. The generic
// widgets already share one frame (PnlWidgetGenericCharts.tsx); these five now
// share this one, so a fix lands once.
//
// WHAT THE FRAME ADDS THAT NONE OF THEM HAD. A caption under the title saying
// what one mark on the chart IS, in the words groupByVocabulary.ts already
// uses everywhere else on the page. Josh's team asked for exactly this: a
// reader who did not build the dashboard should be able to tell what a bar
// stands for without being told.
import type { ReactNode } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { SearchX } from "lucide-react";
import { CHART_EMPTY_HINT, CHART_EMPTY_TITLE } from "../../features/pnlAnalytics/chartEmptiness";
import { PNL_ENTRANCE_SPRING } from "../../features/pnlAnalytics/pnlMotion";
import "./PnlWidgetCharts.css";

interface PnlChartHeadProps {
  /** The lucide icon for this chart, already sized and coloured by the caller. */
  icon: ReactNode;
  title: string;
  /** What one mark on this chart is, and how to open it. One short sentence or two. */
  caption: ReactNode;
  /** The chart's own controls: a limit select, an axis toggle, a project picker. */
  children?: ReactNode;
  /** SEQ-240: the "How is this worked out" trigger, placed beside the title. */
  info?: ReactNode;
}

/**
 * A chart card's title block.
 *
 * The caption sits INSIDE this block rather than between the header and the
 * chart, which is not only a layout preference: the card is a flex column whose
 * chart body is its second child, and a caption as a sibling would have moved
 * the body out from under the rule that gives it its height. Naming the body
 * (.pnl-chart-body) fixed that properly; keeping the caption with the title it
 * belongs to is what the design wanted anyway.
 */
export function PnlChartHead({ icon, title, caption, children, info }: PnlChartHeadProps) {
  return (
    <div className="pnl-chart-head">
      <div className="pnl-chart-head-text">
        <span className="pnl-chart-head-title">
          {icon}
          {title}
          {info}
        </span>
        <p className="pnl-chart-head-caption">{caption}</p>
      </div>
      {children ? <div className="pnl-chart-head-controls">{children}</div> : null}
    </div>
  );
}

/**
 * What a chart with nothing to draw shows.
 *
 * Replaces three different empty treatments and two missing ones. The icon
 * names the condition (a search that found nothing) rather than decorating it,
 * and the second line says what to change, because "no data" on its own leaves
 * the reader unsure whether the page is broken.
 */
export function PnlChartEmpty({ height = 280 }: { height?: number }) {
  const reduce = useReducedMotion() ?? false;
  return (
    <div className="pnl-chart-body pnl-chart-empty" style={{ height }}>
      <motion.div
        className="pnl-chart-empty-inner"
        initial={reduce ? false : { opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={PNL_ENTRANCE_SPRING}
      >
        <SearchX size={22} aria-hidden="true" className="pnl-chart-empty-icon" />
        <p className="pnl-chart-empty-title">{CHART_EMPTY_TITLE}</p>
        <p className="pnl-chart-empty-hint">{CHART_EMPTY_HINT}</p>
      </motion.div>
    </div>
  );
}

export interface PinnedProject {
  projectId: string;
  projectName: string;
  /** The real figures on whichever axes were run past, formatted. Never a pinned coordinate. */
  reading: string;
}

interface PnlAxisNoteProps {
  /** What was run past, as a noun phrase: "the profit axis", "both axes of this chart". */
  beyond: string;
  projects: PinnedProject[];
  onOpenProject: (projectId: string) => void;
}

/**
 * The line under a clamped chart, naming every project that ran off it.
 *
 * This is the half of the clamp that makes it honest. The axis is cut so the
 * other twenty projects can be compared; this says, in words and with the real
 * figures, which ones were cut and by how much, and each name opens that
 * project. Without it the clamp would be hiding the worst project on the page,
 * which is the opposite of what the page is for.
 */
export function PnlAxisNote({ beyond, projects, onOpenProject }: PnlAxisNoteProps) {
  const reduce = useReducedMotion() ?? false;
  if (projects.length === 0) return null;

  const one = projects.length === 1;
  return (
    <motion.p
      className="pnl-axis-note"
      initial={reduce ? false : { opacity: 0, y: -4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={PNL_ENTRANCE_SPRING}
    >
      <span className="pnl-axis-note-marker" aria-hidden="true" />
      <span>
        {one ? "One project runs" : `${projects.length} projects run`} past {beyond} and {one ? "is" : "are"} drawn on
        the edge instead. {one ? "Its real figure" : "Their real figures"}:{" "}
        {projects.map((project, index) => (
          <span key={project.projectId}>
            {index > 0 ? ", " : ""}
            <button type="button" className="pnl-axis-note-link" onClick={() => onOpenProject(project.projectId)}>
              {project.projectName}
            </button>{" "}
            at {project.reading}
          </span>
        ))}
        .
      </span>
    </motion.p>
  );
}
